
import React from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import MDAlert from "components/MDAlert";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import EditIcon from "@mui/icons-material/Edit";
import MDButton from "components/MDButton";

import moment from "moment";

// services
import { getPackages, getStatusBreakdown, getCountries } from "network/service";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useEffect, useState, useContext } from "react";
import { AuthContext } from "context/auth.context";
import { useNavigate } from "react-router-dom";

// Modal
import EditDBModal from "layouts/modals/components/Edit_DBModal";

// ── Card component: shows status count + per-carrier chip breakdown ──────────
function StatusCarrierCard({ color, icon, title, total, carriers, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{ cursor: onClick ? "pointer" : "default", "&:hover": onClick ? { boxShadow: 4 } : {} }}
    >
      <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
        <MDBox
          variant="gradient"
          bgColor={color}
          color="white"
          coloredShadow={color}
          borderRadius="xl"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="4rem"
          height="4rem"
          mt={-3}
        >
          <Icon fontSize="medium" color="inherit">
            {icon}
          </Icon>
        </MDBox>
        <MDBox textAlign="right" lineHeight={1.25}>
          <MDTypography variant="button" fontWeight="light" color="text">
            {title}
          </MDTypography>
          <MDTypography variant="h4">{total}</MDTypography>
        </MDBox>
      </MDBox>
      <Divider sx={{ my: 0.5 }} />
      <MDBox pb={1.5} px={2} minHeight={36} display="flex" alignItems="center" flexWrap="wrap" sx={{ gap: 0.5 }}>
        {Object.keys(carriers).length > 0 ? (
          Object.entries(carriers).map(([carrier, count]) => (
            <Chip
              key={carrier}
              label={`${carrier}: ${count}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: "0.65rem", height: 22, borderRadius: "6px" }}
            />
          ))
        ) : (
          <MDTypography variant="caption" color="text">
            —
          </MDTypography>
        )}
      </MDBox>
    </Card>
  );
}

// ── Status card config (order + style) ───────────────────────────────────────
const STATUS_CARDS = [
  { key: "Pending",   label: "Pending",    color: "primary", icon: "timer",     statusCode: 0 },
  { key: "Picked Up", label: "Picked Up",  color: "info",    icon: "business",  statusCode: 5 },
  { key: "Intransit", label: "In-Transit", color: "success", icon: "unarchive", statusCode: 1 },
  { key: "Delivered", label: "Delivered",  color: "success", icon: "check",     statusCode: 3 },
  { key: "On Hold",   label: "On Hold",    color: "dark",    icon: "shop",      statusCode: 2 },
  { key: "Issue",     label: "Issue",      color: "error",   icon: "error",     statusCode: 4 },
];

// ── Dashboard ─────────────────────────────────────────────────────────────────
function Dashboard() {
  let { userDoc } = useContext(AuthContext);
  const navigate = useNavigate();

  const initialFilters = JSON.parse(localStorage.getItem("filters")) || {
    from: new Date(),
    to: new Date(),
    status: [],
  };

  const [filters, setFilters] = useState(initialFilters);
  const [dElems, setDelems] = useState([]);
  const [dTableElement, setDTableElement] = useState(null);
  const [editDTableOpen, setEditDTableOpen] = useState(false);
  const [toastMsg, setToastMsg] = React.useState("");
  const [toastType, setToastType] = React.useState("");
  const [showToast, setShowToast] = useState(false);
  const [breakdown, setBreakdown] = useState({});
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());

  const closeDBModal = () => setEditDTableOpen(false);

  const getData = () => {
    const dateParam = `createdAt_gte=${moment(filters.from).format("YYYY-MM-DD")}&createdAt_lte=${moment(filters.to).add("day", 1).format("YYYY-MM-DD")}`;
    getStatusBreakdown(dateParam)
      .then((resp) => {
        console.log("Status breakdown", resp.data);
        setBreakdown(resp.data);
      })
      .catch((err) => console.log(err));
  };

  const toast = (msg, type) => {
    setShowToast(true);
    setToastMsg(msg);
    setToastType(type);
    setTimeout(() => setShowToast(false), 3000);
  };

  useEffect(() => {
    console.log("Received User Doc", userDoc);
    getData();
  }, [setBreakdown]);

  useEffect(() => {
    localStorage.setItem("filters", JSON.stringify(filters));
  }, [filters]);

  // Helper: safely read a status entry from the API response
  const entry = (key) =>
    breakdown[key] || { total: 0, carriers: {} };

  return (
    <DashboardLayout>
      {editDTableOpen && (
        <EditDBModal toast={toast} details={dTableElement} handleClose={closeDBModal} />
      )}
      {showToast && (
        <div className="toast-display">
          <MDAlert color={toastType}>{toastMsg}</MDAlert>
        </div>
      )}
      <DashboardNavbar />
      <MDBox py={3}>

        {/* ── Date filter ── */}
        <MDBox mb={5}>
          <MDInput
            type="date"
            label="From"
            color="info"
            style={{ margin: 5 }}
            value={moment(fromDate).format("YYYY-MM-DD")}
            onChange={(e) => {
              setFromDate(e.target.valueAsDate);
              setFilters({ ...filters, from: e.target.valueAsDate });
            }}
          />
          <MDInput
            type="date"
            label="To"
            style={{ margin: 5 }}
            value={moment(toDate).format("YYYY-MM-DD")}
            onChange={(e) => {
              setToDate(e.target.valueAsDate);
              setFilters({ ...filters, to: e.target.valueAsDate });
            }}
          />
          <MDButton
            variant="text"
            color="info"
            style={{ margin: 5 }}
            onClick={getData}
          >
            Apply
          </MDButton>
        </MDBox>

        {/* ── Status cards with carrier breakdown ── */}
        <Grid container spacing={3}>
          {STATUS_CARDS.map(({ key, label, color, icon, statusCode }) => (
            <Grid key={key} item xs={12} md={6} lg={4}>
              <MDBox mb={1.5}>
                <StatusCarrierCard
                  color={color}
                  icon={icon}
                  title={label}
                  total={entry(key).total}
                  carriers={entry(key).carriers}
                  onClick={() => {
                    localStorage.setItem(
                      "filters",
                      JSON.stringify({ ...filters, status: [statusCode] })
                    );
                    navigate("/packages");
                  }}
                />
              </MDBox>
            </Grid>
          ))}
        </Grid>

      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
